/**
 * Conversion Bridge Integration
 * Fires client-side conversion pixels to Google Ads and LinkedIn
 * when Flowlitycs detects conversion events.
 *
 * This bypasses server-side CAPI limitations (Google Basic access,
 * LinkedIn Conversions API access denied) by firing directly from
 * the user's browser where the conversion happens.
 *
 * Supports:
 * - Google Ads (gtag.js) — conversion tracking via AW-ID/label
 * - LinkedIn Insight Tag — conversion tracking via partner ID
 * - Facebook Pixel — enhanced matching (supplements server CAPI)
 *
 * Click ID persistence:
 * - Captures gclid, fbclid, li_fat_id from URL on landing
 * - Stores in localStorage for cross-page persistence
 * - Attaches to all events for identity resolution
 */

const STORAGE_KEY = "flw_click_ids";
const STORAGE_TTL = 90 * 24 * 60 * 60 * 1000; // 90 days

class ConversionBridge {
  constructor(config) {
    this.config = config;
    this.platforms = config.get("conversions") || {};
    this.clickIds = {};
    this.initialized = false;
  }

  /**
   * Initialize: capture click IDs, load platform tags
   */
  init() {
    // 1. Capture click IDs from URL and persist
    this.captureClickIds();

    // 2. Load platform tags if configured
    if (this.platforms.google_ads) {
      this.loadGoogleAdsTag();
    }
    if (this.platforms.linkedin) {
      this.loadLinkedInTag();
    }
    if (this.platforms.facebook) {
      this.loadFacebookTag();
    }
    if (this.platforms.tiktok) {
      this.loadTikTokTag();
    }

    this.initialized = true;

    if (this.config.get("debug")) {
      console.log("[Flowlytics:ConversionBridge] Initialized", {
        platforms: Object.keys(this.platforms),
        clickIds: this.clickIds,
      });
    }
  }

  /**
   * Capture click IDs from URL query string and localStorage
   */
  captureClickIds() {
    // Load persisted click IDs
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed.expires > Date.now()) {
          this.clickIds = parsed.ids || {};
        } else {
          localStorage.removeItem(STORAGE_KEY);
        }
      }
    } catch (_) {
      /* localStorage not available */
    }

    // Extract new click IDs from current URL
    try {
      const params = new URLSearchParams(window.location.search);
      const captureMap = {
        gclid: "gclid",
        gbraid: "gbraid",
        wbraid: "wbraid",
        fbclid: "fbclid",
        li_fat_id: "li_fat_id",
        ttclid: "ttclid",
        utm_source: "utm_source",
        utm_medium: "utm_medium",
        utm_campaign: "utm_campaign",
        utm_content: "utm_content",
        utm_term: "utm_term",
      };

      for (const [param, key] of Object.entries(captureMap)) {
        const value = params.get(param);
        if (value) {
          this.clickIds[key] = value;
        }
      }
    } catch (_) {
      /* URL parsing failed */
    }

    // Also capture LinkedIn li_fat_id from cookie
    try {
      const cookies = document.cookie.split(";");
      for (const cookie of cookies) {
        const [name, value] = cookie.trim().split("=");
        if (name === "li_fat_id" && value) {
          this.clickIds.li_fat_id = decodeURIComponent(value);
        }
      }
    } catch (_) {
      /* cookie parsing failed */
    }

    // Persist updated click IDs
    this.persistClickIds();
  }

  /**
   * Persist click IDs to localStorage
   */
  persistClickIds() {
    if (Object.keys(this.clickIds).length === 0) return;
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          ids: this.clickIds,
          expires: Date.now() + STORAGE_TTL,
          updated: new Date().toISOString(),
        }),
      );
    } catch (_) {
      /* localStorage not available */
    }
  }

  /**
   * Get click IDs to attach to pixel events
   * Called by EventQueue before sending
   */
  getClickIds() {
    return { ...this.clickIds };
  }

  /**
   * Handle a tracked event — fire platform conversions if applicable
   * @param {string} eventType - Event type (purchase, form_submit, etc.)
   * @param {Object} properties - Event properties
   */
  onEvent(eventType, properties = {}, eventId = null) {
    if (!this.initialized) return;

    const eventLower = (eventType || "").toLowerCase();

    // Map events to conversion types
    const conversionEvents = {
      purchase: "purchase",
      transaction: "purchase",
      order: "purchase",
      "conversion.purchase": "purchase",
      form_submit: "lead",
      lead: "lead",
      signup: "lead",
      sign_up: "lead",
      trial_registration: "lead",
      "conversion.lead": "lead",
      subscription_activated: "purchase",
      payment_complete: "purchase",
    };

    const conversionType = conversionEvents[eventLower];
    if (!conversionType) return; // Not a conversion event

    const value =
      properties.conversion_value ||
      properties.value ||
      properties.revenue ||
      0;
    const currency = properties.currency || "EUR";

    // Fire to each configured platform (unified logic for all)
    if (this.platforms.google_ads) {
      this.fireGoogleAdsConversion(
        conversionType,
        value,
        currency,
        properties,
        eventId,
      );
    }
    if (this.platforms.facebook) {
      this.fireFacebookConversion(
        conversionType,
        value,
        currency,
        properties,
        eventId,
      );
    }
    if (this.platforms.linkedin) {
      this.fireLinkedInConversion(conversionType, value, currency, properties);
    }
    if (this.platforms.tiktok) {
      this.fireTikTokConversion(
        conversionType,
        value,
        currency,
        properties,
        eventId,
      );
    }
  }

  // =========================================================================
  // GOOGLE ADS
  // =========================================================================

  /**
   * Load Google Ads gtag.js
   */
  loadGoogleAdsTag() {
    const { conversionId } = this.platforms.google_ads;
    if (!conversionId || typeof window === "undefined") return;

    // Check if gtag already loaded
    if (window.gtag) return;

    // Load gtag.js
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    window.gtag = function () {
      window.dataLayer.push(arguments);
    };
    window.gtag("js", new Date());
    window.gtag("config", conversionId, { send_page_view: false });
  }

  /**
   * Fire Google Ads conversion
   */
  fireGoogleAdsConversion(type, value, currency, properties, eventId = null) {
    if (!window.gtag) return;

    const { conversionId, conversionLabel, purchaseLabel, leadLabel } =
      this.platforms.google_ads;

    // Select label based on conversion type
    let label;
    if (type === "purchase") {
      label = purchaseLabel || conversionLabel;
    } else if (type === "lead") {
      label = leadLabel || conversionLabel;
    }

    if (!label) return;

    const conversionData = {
      send_to: `${conversionId}/${label}`,
      value: parseFloat(value) || 0,
      currency: currency,
      // The event_id fallback is stable across SDK retries, so Google can
      // dedupe; Date.now() generated a NEW id per attempt (duplicates).
      transaction_id:
        properties.order_id ||
        properties.transaction_id ||
        eventId ||
        `fl_${Date.now()}`,
    };

    window.gtag("event", "conversion", conversionData);

    if (this.config.get("debug")) {
      console.log(
        "[Flowlytics:ConversionBridge] Google Ads conversion fired",
        conversionData,
      );
    }
  }

  // =========================================================================
  // LINKEDIN
  // =========================================================================

  /**
   * Load LinkedIn Insight Tag
   */
  loadLinkedInTag() {
    const { partnerId } = this.platforms.linkedin;
    if (!partnerId || typeof window === "undefined") return;

    // Check if already loaded
    if (window.lintrk) return;

    // LinkedIn Insight Tag initialization
    window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
    window._linkedin_data_partner_ids.push(partnerId);

    (function (l) {
      if (!l) {
        window.lintrk = function (a, b) {
          window.lintrk.q.push([a, b]);
        };
        window.lintrk.q = [];
      }
      var s = document.getElementsByTagName("script")[0];
      var b = document.createElement("script");
      b.type = "text/javascript";
      b.async = true;
      b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
      s.parentNode.insertBefore(b, s);
    })(window.lintrk);
  }

  /**
   * Fire LinkedIn conversion
   */
  fireLinkedInConversion(type, value, currency, properties) {
    if (!window.lintrk) return;

    const { conversionIds } = this.platforms.linkedin;
    if (!conversionIds) return;

    // Select conversion ID based on type
    const conversionId =
      type === "purchase" ? conversionIds.purchase : conversionIds.lead;

    if (!conversionId) return;

    const trackData = { conversion_id: conversionId };
    if (value > 0) {
      trackData.value = parseFloat(value) || 0;
      trackData.currency = currency;
    }
    window.lintrk("track", trackData);

    if (this.config.get("debug")) {
      console.log("[Flowlytics:ConversionBridge] LinkedIn conversion fired", {
        conversion_id: conversionId,
        type,
        value,
        currency,
      });
    }
  }

  // =========================================================================
  // FACEBOOK
  // =========================================================================

  /**
   * Load Facebook Pixel
   */
  loadFacebookTag() {
    const { pixelId } = this.platforms.facebook;
    if (!pixelId || typeof window === "undefined") return;
    if (window.fbq) return; // Already loaded (e.g. by the site's own FB pixel code)

    !(function (f, b, e, v, n, t, s) {
      if (f.fbq) return;
      n = f.fbq = function () {
        n.callMethod
          ? n.callMethod.apply(n, arguments)
          : n.queue.push(arguments);
      };
      if (!f._fbq) f._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = "2.0";
      n.queue = [];
      t = b.createElement(e);
      t.async = !0;
      t.src = v;
      s = b.getElementsByTagName(e)[0];
      s.parentNode.insertBefore(t, s);
    })(
      window,
      document,
      "script",
      "https://connect.facebook.net/en_US/fbevents.js",
    );
    window.fbq("init", pixelId);
    window.fbq("track", "PageView");
  }

  /**
   * Fire Facebook Pixel conversion
   */
  fireFacebookConversion(type, value, currency, properties, eventId = null) {
    if (!window.fbq) return;

    const eventName = type === "purchase" ? "Purchase" : "Lead";

    window.fbq(
      "track",
      eventName,
      {
        value: parseFloat(value) || 0,
        currency: currency,
        content_name: properties.product || properties.content_name,
      },
      // Shared with the server-side CAPI event: Meta dedupes on (event_name, eventID)
      eventId ? { eventID: eventId } : undefined,
    );

    if (this.config.get("debug")) {
      console.log("[Flowlytics:ConversionBridge] Facebook conversion fired", {
        eventName,
        value,
      });
    }
  }

  // =========================================================================
  // TIKTOK
  // =========================================================================

  /**
   * Load TikTok Pixel
   */
  loadTikTokTag() {
    const { pixelId } = this.platforms.tiktok;
    if (!pixelId || typeof window === "undefined") return;
    if (window.ttq) return;

    !(function (w, d, t) {
      w.TiktokAnalyticsObject = t;
      var ttq = (w[t] = w[t] || []);
      ttq.methods = [
        "page",
        "track",
        "identify",
        "instances",
        "debug",
        "on",
        "off",
        "once",
        "ready",
        "alias",
        "group",
        "enableCookie",
        "disableCookie",
        "holdConsent",
        "revokeConsent",
        "grantConsent",
      ];
      ttq.setAndDefer = function (t, e) {
        t[e] = function () {
          t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
        };
      };
      for (var i = 0; i < ttq.methods.length; i++)
        ttq.setAndDefer(ttq, ttq.methods[i]);
      ttq.instance = function (t) {
        for (var e = ttq._i[t] || [], n = 0; n < ttq.methods.length; n++)
          ttq.setAndDefer(e, ttq.methods[n]);
        return e;
      };
      ttq.load = function (e, n) {
        var r = "https://analytics.tiktok.com/i18n/pixel/events.js",
          o = n && n.partner;
        ttq._i = ttq._i || {};
        ttq._i[e] = [];
        ttq._i[e]._u = r;
        ttq._t = ttq._t || {};
        ttq._t[e] = +new Date();
        ttq._o = ttq._o || {};
        ttq._o[e] = n || {};
        n = d.createElement("script");
        n.type = "text/javascript";
        n.async = !0;
        n.src = r + "?sdkid=" + e + "&lib=" + t;
        d.getElementsByTagName("head")[0].appendChild(n);
      };
      ttq.load(pixelId);
      ttq.page();
    })(window, document, "ttq");
  }

  /**
   * Fire TikTok conversion
   */
  fireTikTokConversion(type, value, currency, properties, eventId = null) {
    if (!window.ttq) return;

    const eventMap = {
      purchase: "CompletePayment",
      lead: "SubmitForm",
    };
    const eventName = eventMap[type] || type;

    window.ttq.track(
      eventName,
      {
        value: parseFloat(value) || 0,
        currency: currency,
        content_id: properties.product || properties.content_id,
        content_type: "product",
      },
      // Shared with the server-side Events API send: TikTok dedupes on event_id
      eventId ? { event_id: eventId } : undefined,
    );

    if (this.config.get("debug")) {
      console.log("[Flowlytics:ConversionBridge] TikTok conversion fired", {
        eventName,
        value,
      });
    }
  }
}

export default ConversionBridge;
